import { supabase } from '../lib/supabaseClient';

export interface LicensingStatus {
  allowed: boolean;
  reason?:
    | 'trial_expired'
    | 'payment_grace_expired'
    | 'subscription_inactive'
    | 'seat_limit_exceeded'
    | 'salon_not_found'
    | 'salon_membership_required'
    | 'account_access_denied'
    | 'salon_context_required'
    | 'licensing_lookup_failed';
  trial_ends_at?: string;
  payment_grace_until?: string;
  seat_limit?: number;
  current_seats?: number;
  plan_code?: string;
  billing_status?: string;
}

/**
 * Check the current licensing status for the authenticated user's salon
 * This is the core function that validates access permissions
 */
export async function checkLicensing(salonId: string | null): Promise<LicensingStatus> {
  if (!salonId) {
    return { allowed: false, reason: 'salon_context_required' };
  }

  const { data: sessionData } = await supabase.auth.getSession();

  if (!sessionData?.session?.access_token) {
    return {
      allowed: false,
      reason: 'salon_not_found'
    };
  }

  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/check-licensing`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${sessionData.session.access_token}`
      },
      body: JSON.stringify({ salon_id: salonId })
    }
  );

  if (!response.ok) {
    throw new Error(`Licensing check failed with status ${response.status}`);
  }

  const result: LicensingStatus = await response.json();
  return result;
}

/**
 * Check if a specific feature is available for the current plan
 * Uses the backend RPC function for feature-level access control
 */
export async function checkFeatureAccess(feature: string, salonId?: string | null): Promise<boolean> {
  try {
    const targetSalonId = typeof salonId === 'string' && salonId.trim() ? salonId : await getCurrentSalonId();

    if (!targetSalonId) {
      return false;
    }

    const { data, error } = await supabase
      .rpc('check_feature_access', {
        p_salon_id: targetSalonId,
        p_feature_name: feature
      });

    if (error) {
      console.error('Error checking feature access:', error);
      return false;
    }

    return data === true;
  } catch (error) {
    console.error('Error checking feature access:', error);
    return false;
  }
}

/**
 * Get current salon usage statistics
 * Provides metrics for plan limit enforcement
 */
export async function getSalonUsage(): Promise<any> {
  try {
    const { data, error } = await supabase
      .rpc('get_salon_usage', {
        p_salon_id: await getCurrentSalonId()
      });

    if (error) {
      console.error('Error getting salon usage:', error);
      return {};
    }

    return data || {};
  } catch (error) {
    console.error('Error getting salon usage:', error);
    return {};
  }
}

/**
 * Helper function to get current salon ID
 * Internal utility for other licensing functions
 */
async function getCurrentSalonId(): Promise<string | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data: salonUser } = await supabase
      .from('salon_users')
      .select('salon_id')
      .eq('user_id', user.id)
      .limit(1)
      .maybeSingle();

    return salonUser?.salon_id || null;
  } catch (error) {
    console.error('Error getting salon ID:', error);
    return null;
  }
}
